/**
 * Product page functionality
 * Handles variant selection, image gallery, and add to cart
 */

class ProductPage {
  constructor() {
    this.product = window.productData || {};
    this.currentVariant = this.product.selected_or_first_available_variant;
    
    this.initImageGallery();
    this.initVariantSelection();
    this.initQuantitySelector();
    this.initAddToCart();
    this.initTabs();
    this.initAccordions();
  }

  initImageGallery() {
    const mainImage = document.getElementById('main-product-image');
    const thumbnails = document.querySelectorAll('.product-media__thumbnail');
    
    if (!mainImage || !thumbnails.length) return;

    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Update main image
        const newSrc = thumbnail.querySelector('img').src.replace('_80x80', '_800x800');
        const newAlt = thumbnail.querySelector('img').alt;
        
        mainImage.src = newSrc;
        mainImage.alt = newAlt;
        
        // Update active thumbnail
        thumbnails.forEach(t => t.classList.remove('active'));
        thumbnail.classList.add('active');
      });
    });

    // Keyboard navigation for thumbnails
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          thumbnail.click();
        }
        
        if (e.key === 'ArrowRight' && index < thumbnails.length - 1) {
          thumbnails[index + 1].focus();
        }
        
        if (e.key === 'ArrowLeft' && index > 0) {
          thumbnails[index - 1].focus();
        }
      });
    });
  }

  initVariantSelection() {
    const variantSelectors = document.querySelectorAll('[name^="options"]');
    const priceElement = document.querySelector('.product-info__price');
    const addToCartButton = document.querySelector('.product-form__cart-button');
    const skuElement = document.querySelector('.product-info__sku');
    
    if (!variantSelectors.length) return;

    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        this.updateVariant();
      });
    });

    // Option value buttons
    const optionButtons = document.querySelectorAll('.product-form__option-value');
    optionButtons.forEach(button => {
      button.addEventListener('click', () => {
        const option = button.closest('.product-form__option');
        const input = option.querySelector('input[type="hidden"]');
        
        if (input && !button.classList.contains('unavailable')) {
          // Update selection
          option.querySelectorAll('.product-form__option-value').forEach(b => {
            b.classList.remove('selected');
          });
          button.classList.add('selected');
          
          // Update hidden input
          input.value = button.dataset.value;
          input.dispatchEvent(new Event('change'));
        }
      });
    });
  }

  updateVariant() {
    const selectedOptions = Array.from(document.querySelectorAll('[name^="options"]'))
      .map(select => select.value);
    
    // Find matching variant
    const variant = this.product.variants.find(v => {
      return v.options.every((option, index) => option === selectedOptions[index]);
    });

    if (variant) {
      this.currentVariant = variant;
      this.updatePrice(variant);
      this.updateSku(variant);
      this.updateAddToCartButton(variant);
      this.updateUrl(variant);
      this.updateImage(variant);
      this.updateVariantId(variant);
    }
  }

  updatePrice(variant) {
    const priceElement = document.querySelector('.product-info__price');
    if (!priceElement) return;

    const price = this.formatMoney(variant.price);
    const comparePrice = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : null;

    let priceHTML = '';
    
    if (comparePrice && variant.compare_at_price > variant.price) {
      priceHTML = `
        <div class="price">
          <span class="price__sale">${price}</span>
          <span class="price__compare-at">${comparePrice}</span>
          <span class="price__badge">Sale</span>
        </div>
      `;
    } else {
      priceHTML = `<div class="price"><span class="price__regular">${price}</span></div>`;
    }

    priceElement.innerHTML = priceHTML;
  }

  updateSku(variant) {
    const skuElement = document.querySelector('.product-info__sku');
    if (skuElement && variant.sku) {
      skuElement.textContent = `SKU: ${variant.sku}`;
    }
  }

  updateAddToCartButton(variant) {
    const button = document.querySelector('.product-form__cart-button');
    if (!button) return;

    const buttonText = button.querySelector('.button-text') || button;
    
    if (variant.available) {
      button.disabled = false;
      button.classList.remove('sold-out');
      buttonText.textContent = 'Add to Cart';
    } else {
      button.disabled = true;
      button.classList.add('sold-out');
      buttonText.textContent = 'Sold Out';
    }
  }

  updateUrl(variant) {
    if (variant && variant.id) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  updateImage(variant) {
    if (!variant.featured_image) return;

    const mainImage = document.getElementById('main-product-image');
    if (mainImage) {
      mainImage.src = variant.featured_image.src;
      mainImage.alt = variant.featured_image.alt;
    }
  }

  updateVariantId(variant) {
    const idInput = document.querySelector('.product-variant-id');
    if (idInput) {
      idInput.value = variant.id;
    }
  }

  initQuantitySelector() {
    const quantityInput = document.querySelector('.quantity-selector input');
    const increaseButton = document.querySelector('.quantity-selector .quantity-increase');
    const decreaseButton = document.querySelector('.quantity-selector .quantity-decrease');

    if (!quantityInput) return;

    if (increaseButton) {
      increaseButton.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 1;
        quantityInput.value = currentValue + 1;
      });
    }

    if (decreaseButton) {
      decreaseButton.addEventListener('click', () => {
        const currentValue = parseInt(quantityInput.value) || 1;
        if (currentValue > 1) {
          quantityInput.value = currentValue - 1;
        }
      });
    }

    // Validate input
    quantityInput.addEventListener('change', () => {
      const value = parseInt(quantityInput.value);
      if (isNaN(value) || value < 1) {
        quantityInput.value = 1;
      }
    });
  }

  initAddToCart() {
    const form = document.querySelector('.product-form');
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const formData = new FormData(form);
      const button = form.querySelector('.product-form__cart-button');
      const originalHTML = button.innerHTML;

      try {
        button.disabled = true;
        button.innerHTML = '<span class="button-text">Adding...</span>';

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        const result = await response.json();
        this.showAddToCartSuccess(result);
        this.updateCartCount();
      } catch (error) {
        console.error('Add to cart error:', error);
        this.showAddToCartError();
      } finally {
        button.disabled = false;
        button.innerHTML = originalHTML;
      }
    });
  }

  showAddToCartSuccess(item) {
    // Show success notification
    const notification = document.createElement('div');
    notification.className = 'add-to-cart-notification success';
    notification.innerHTML = `
      <div class="notification-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
        <span>Added to cart successfully!</span>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  showAddToCartError() {
    const notification = document.createElement('div');
    notification.className = 'add-to-cart-notification error';
    notification.innerHTML = `
      <div class="notification-content">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
        <span>Failed to add to cart. Please try again.</span>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const countElements = document.querySelectorAll('.header__cart-count');
        countElements.forEach(element => {
          element.textContent = cart.item_count;
          element.style.display = cart.item_count > 0 ? 'block' : 'none';
        });
      })
      .catch(error => console.error('Error updating cart count:', error));
  }

  initTabs() {
    const tabs = document.querySelectorAll('.product-details__tab');
    const contents = document.querySelectorAll('.product-details__content');

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        // Remove active from all tabs and contents
        tabs.forEach(t => t.classList.remove('active'));
        contents.forEach(c => c.classList.remove('active'));

        // Add active to clicked tab and corresponding content
        tab.classList.add('active');
        if (contents[index]) {
          contents[index].classList.add('active');
        }
      });
    });
  }

  initAccordions() {
    const accordionHeaders = document.querySelectorAll('.accordion__header');

    accordionHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const content = header.nextElementSibling;
        const isExpanded = header.getAttribute('aria-expanded') === 'true';

        // Toggle aria-expanded
        header.setAttribute('aria-expanded', !isExpanded);

        // Toggle content
        if (content) {
          content.classList.toggle('active');
        }
      });
    });
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ProductPage();
});

// Add to cart notification styles
const notificationStyles = `
  .add-to-cart-notification {
    position: fixed;
    top: 20px;
    right: 20px;
    background: var(--color-success);
    color: white;
    padding: var(--spacing-md);
    border-radius: var(--border-radius-md);
    box-shadow: var(--shadow-lg);
    z-index: 1000;
    animation: slideIn 0.3s ease;
  }

  .add-to-cart-notification.error {
    background: var(--color-error);
  }

  .notification-content {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

// Inject styles
if (!document.getElementById('product-notification-styles')) {
  const style = document.createElement('style');
  style.id = 'product-notification-styles';
  style.textContent = notificationStyles;
  document.head.appendChild(style);
}
